const DEFAULT_CONTEXT = {
  nivel: "administrativo",
  key: "administrativo",
  label: "Administrativo general",
  proyectoId: "",
  proyectoNombre: "",
  fraccionamientoId: "",
  fraccionamientoNombre: "",
  obraId: "",
  obraNombre: "",
  propiedadId: "",
  propiedadFolio: "",
};

export const safeMoneyNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^\d.-]/g, "");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapGet = (map, id) => {
  if (!id || !map) return null;
  if (map instanceof Map) return map.get(id) || null;
  return map[id] || null;
};

const getPropiedadLabel = (propiedad, fallbackId = "") =>
  propiedad?.folio ||
  propiedad?.codigoHumano ||
  propiedad?.nombre ||
  fallbackId ||
  "";

export const resolverContextoFinanciero = (item = {}, catalogos = {}) => {
  const obra = mapGet(catalogos.obras, item.obraId);
  const propiedad = mapGet(catalogos.propiedades, item.propiedadId || obra?.propiedadId);
  const proyecto = mapGet(catalogos.proyectos, item.proyectoId || obra?.proyectoId || propiedad?.proyectoId);
  const fraccionamiento = mapGet(
    catalogos.fraccionamientos,
    item.fraccionamientoId || obra?.fraccionamientoId || propiedad?.fraccionamientoId
  );

  const ctx = {
    ...DEFAULT_CONTEXT,
    proyectoId: item.proyectoId || obra?.proyectoId || propiedad?.proyectoId || "",
    proyectoNombre:
      item.proyectoNombre ||
      obra?.proyectoNombre ||
      propiedad?.proyectoNombre ||
      proyecto?.nombre ||
      "",
    fraccionamientoId:
      item.fraccionamientoId || obra?.fraccionamientoId || propiedad?.fraccionamientoId || "",
    fraccionamientoNombre:
      item.fraccionamientoNombre ||
      obra?.fraccionamientoNombre ||
      propiedad?.fraccNombre ||
      propiedad?.fraccionamientoNombre ||
      fraccionamiento?.nombre ||
      "",
    obraId: item.obraId || "",
    obraNombre: item.obraNombre || obra?.nombre || "",
    propiedadId: item.propiedadId || obra?.propiedadId || "",
    propiedadFolio:
      item.propiedadFolio ||
      obra?.propiedadFolio ||
      getPropiedadLabel(propiedad, item.propiedadId || obra?.propiedadId || ""),
  };

  if (ctx.propiedadId) {
    return {
      ...ctx,
      nivel: "propiedad",
      key: `propiedad:${ctx.propiedadId}`,
      label: ctx.propiedadFolio || ctx.propiedadId,
    };
  }

  if (ctx.obraId) {
    return {
      ...ctx,
      nivel: "obra",
      key: `obra:${ctx.obraId}`,
      label: ctx.obraNombre || ctx.obraId,
    };
  }

  if (ctx.fraccionamientoId) {
    return {
      ...ctx,
      nivel: "fraccionamiento",
      key: `fraccionamiento:${ctx.fraccionamientoId}`,
      label: ctx.fraccionamientoNombre || ctx.fraccionamientoId,
    };
  }

  if (ctx.proyectoId) {
    return {
      ...ctx,
      nivel: "proyecto",
      key: `proyecto:${ctx.proyectoId}`,
      label: ctx.proyectoNombre || ctx.proyectoId,
    };
  }

  return DEFAULT_CONTEXT;
};

const emptyGroup = (key, label, nivel) => ({
  key,
  label,
  nivel,
  total: 0,
  capitalizable: 0,
  noCapitalizable: 0,
  saldoRestante: 0,
  count: 0,
});

const addToMapGroup = (target, key, label, nivel, amount, saldo, capitalizable) => {
  if (!key) return;
  if (!target[key]) target[key] = emptyGroup(key, label || key, nivel);
  target[key].total += amount;
  target[key].saldoRestante += saldo;
  target[key].count += 1;
  if (capitalizable) {
    target[key].capitalizable += amount;
  } else {
    target[key].noCapitalizable += amount;
  }
};

export const construirResumenContextoFinanciero = (items = [], catalogos = {}) => {
  const resumen = {
    totalGastos: 0,
    totalSaldoRestante: 0,
    totalCapitalizable: 0,
    totalNoCapitalizable: 0,
    clasificados: 0,
    sinClasificar: 0,
    porNivel: {
      proyecto: emptyGroup("nivel:proyecto", "Proyecto", "proyecto"),
      fraccionamiento: emptyGroup("nivel:fraccionamiento", "Fraccionamiento", "fraccionamiento"),
      obra: emptyGroup("nivel:obra", "Obra", "obra"),
      propiedad: emptyGroup("nivel:propiedad", "Propiedad/lote", "propiedad"),
      administrativo: emptyGroup("nivel:administrativo", "Administrativo general", "administrativo"),
    },
    porProyecto: {},
    porFraccionamiento: {},
    porObra: {},
    porPropiedad: {},
    sinClasificarItems: [],
  };

  items.forEach((item) => {
    const importe = safeMoneyNumber(item.importe);
    const saldo = safeMoneyNumber(item.saldoRestante);
    const capitalizable = item.capitalizable === true;
    const contexto = resolverContextoFinanciero(item, catalogos);
    const isClasificado = contexto.nivel !== "administrativo";

    resumen.totalGastos += importe;
    resumen.totalSaldoRestante += saldo;
    resumen.totalCapitalizable += capitalizable ? importe : 0;
    resumen.totalNoCapitalizable += capitalizable ? 0 : importe;
    resumen.clasificados += isClasificado ? 1 : 0;
    resumen.sinClasificar += isClasificado ? 0 : 1;

    addToMapGroup(
      resumen.porNivel,
      contexto.nivel,
      resumen.porNivel[contexto.nivel]?.label || contexto.nivel,
      contexto.nivel,
      importe,
      saldo,
      capitalizable
    );

    if (contexto.proyectoId) {
      addToMapGroup(
        resumen.porProyecto,
        contexto.proyectoId,
        contexto.proyectoNombre || contexto.proyectoId,
        "proyecto",
        importe,
        saldo,
        capitalizable
      );
    }

    if (contexto.fraccionamientoId) {
      addToMapGroup(
        resumen.porFraccionamiento,
        contexto.fraccionamientoId,
        contexto.fraccionamientoNombre || contexto.fraccionamientoId,
        "fraccionamiento",
        importe,
        saldo,
        capitalizable
      );
    }

    if (contexto.obraId) {
      addToMapGroup(
        resumen.porObra,
        contexto.obraId,
        contexto.obraNombre || contexto.obraId,
        "obra",
        importe,
        saldo,
        capitalizable
      );
    }

    if (contexto.propiedadId) {
      addToMapGroup(
        resumen.porPropiedad,
        contexto.propiedadId,
        contexto.propiedadFolio || contexto.propiedadId,
        "propiedad",
        importe,
        saldo,
        capitalizable
      );
    }

    if (!isClasificado) {
      resumen.sinClasificarItems.push({
        id: item.id,
        proveedorNombre: item.proveedorNombre || "",
        referencia: item.referencia || "",
        importe,
        saldoRestante: saldo,
      });
    }
  });

  return resumen;
};

export const topFinancialGroups = (groups = {}, limit = 5) =>
  Object.values(groups)
    .sort((a, b) => safeMoneyNumber(b.total) - safeMoneyNumber(a.total))
    .slice(0, limit);
